/* GENERATED from src/js/ndog-collection-filters.js — edit the source, then run `make js`. Never edit this file by hand. */
/*
  <ndog-collection-filters> — dynamic filtering for the ndog collection page
  (sections/section-ndog-collection.liquid). Progressive enhancement on top
  of a plain GET form: intercepts filter/sort changes, fetches the rendered
  section via the Section Rendering API, swaps the content in place and keeps
  the URL in sync (shareable links, working back button). On any fetch error
  it falls back to a normal page navigation, so filtering never breaks.
*/
if (!customElements.get('ndog-collection-filters')) {
  customElements.define(
    'ndog-collection-filters',
    class NdogCollectionFilters extends HTMLElement {
      // Bind listeners; the window/document/media listeners are removed on disconnect
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.abortController = null;
        // Default state depends on the viewport: from tablet up (750px, where
        // the panel is an inline collapse) it starts OPEN, below that it is the
        // off-canvas drawer and must start closed.
        this.desktopQuery = window.matchMedia('(min-width: 750px)');
        this.panelOpen = this.desktopQuery.matches;
        this.onPopState = this.onPopState.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);
        this.addEventListener('change', this.onChange);
        this.addEventListener('submit', this.onSubmit);
        this.addEventListener('click', this.onClick);
        window.addEventListener('popstate', this.onPopState);
        document.addEventListener('keydown', this.onKeydown);
        this.desktopQuery.addEventListener('change', this.onBreakpointChange);
        // is-enhanced switches the CSS from "panel always open" (no-JS) to
        // collapsible (desktop) / slide-in drawer (mobile)
        this.classList.add('is-enhanced');
        this.setupPanel();
      }

      // Progressive enhancement: without JS the panel is simply open and the
      // toggle/close/backdrop/"Alle" elements stay hidden. With JS they appear and the
      // panel state lives in the is-panel-open host class (survives content
      // swaps; the CSS animates the mobile drawer from it). Chips are the
      // closed-state view: shown only while the panel is closed. The body
      // class lets the CSS lock page scroll while the mobile drawer is open.
      setupPanel() {
        const panel = this.querySelector('[data-filter-panel]');
        if (!panel) return;
        this.querySelectorAll('[data-filter-toggle], [data-clear-group]').forEach((element) => {
          element.hidden = false;
          if (element.hasAttribute('aria-controls')) {
            element.setAttribute('aria-expanded', String(this.panelOpen));
          }
        });
        this.classList.toggle('is-panel-open', this.panelOpen);
        // scroll lock is a drawer-only concern — never set it on desktop
        document.body.classList.toggle(
          'ndog-collection-panel-open',
          this.panelOpen && !this.desktopQuery.matches
        );
        const chips = this.querySelector('[data-filter-chips]');
        if (chips) chips.hidden = this.panelOpen;
      }

      // Open/close the panel ("Filter" button, close button, backdrop, Escape).
      // Opening moves focus to the close button inside the panel; closing from
      // inside (or via Escape/backdrop) moves it back to the "Filter" button.
      togglePanel(clickedElement) {
        this.panelOpen = !this.panelOpen;
        this.setupPanel();
        if (this.panelOpen) {
          const close = this.querySelector('.ndog-collection__panel-close');
          if (close) close.focus();
          return;
        }
        if (clickedElement && clickedElement.hasAttribute('aria-controls')) return;
        const opener = this.querySelector('[data-filter-toggle][aria-controls]');
        if (opener) opener.focus();
      }

      // Crossing the 750px breakpoint resets the panel to its default state for
      // that viewport — otherwise a resize could leave the mobile drawer open
      // (with backdrop and scroll lock) after collapsing on desktop.
      onBreakpointChange(event) {
        this.panelOpen = event.matches;
        this.setupPanel();
      }

      // Clean up the listeners that outlive this element
      disconnectedCallback() {
        window.removeEventListener('popstate', this.onPopState);
        document.removeEventListener('keydown', this.onKeydown);
        this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
        document.body.classList.remove('ndog-collection-panel-open');
        if (this.abortController) this.abortController.abort();
      }

      // Escape closes the mobile drawer; on desktop the panel is a normal part
      // of the page (open by default), so Escape must not collapse it.
      onKeydown(event) {
        if (event.key !== 'Escape' || !this.panelOpen) return;
        if (this.desktopQuery.matches) return;
        this.togglePanel(null);
      }

      // Filter checkbox or sort select changed → re-render
      onChange(event) {
        const input = event.target;
        if (input.matches('[data-filter-input]')) this.enforceSingleSelect(input);
        if (input.matches('[data-filter-input], [data-sort-select]')) this.applyFilters();
      }

      // No-JS form submit is intercepted once JS is active
      onSubmit(event) {
        event.preventDefault();
        this.applyFilters();
      }

      // Filter button toggles the panel, "Alle" clears its group,
      // reset/chip/pagination links render via fetch
      onClick(event) {
        const toggleButton = event.target.closest('[data-filter-toggle]');
        if (toggleButton) {
          this.togglePanel(toggleButton);
          return;
        }
        const allButton = event.target.closest('[data-clear-group]');
        if (allButton) {
          this.clearGroup(allButton.closest('[data-filter-group]'));
          return;
        }
        const link = event.target.closest('[data-reset-link], .pagination a[href]');
        if (link) {
          event.preventDefault();
          this.renderFromUrl(link.href, true);
        }
      }

      // "Alle" bubble: uncheck every value of its group and re-render
      clearGroup(group) {
        if (!group) return;
        group.querySelectorAll('[data-filter-input]').forEach((box) => (box.checked = false));
        this.applyFilters();
      }

      // In single-select groups a newly checked pill unchecks its siblings
      // (clicking the active pill again simply unchecks it = "all")
      enforceSingleSelect(input) {
        const group = input.closest('[data-filter-group]');
        if (!group || group.dataset.multi === 'true' || !input.checked) return;
        group.querySelectorAll('[data-filter-input]').forEach((box) => {
          if (box !== input) box.checked = false;
        });
      }

      // Collect checked filters + sort into a clean URL on the collection path
      buildUrl() {
        const form = this.querySelector('form');
        const params = new URLSearchParams();
        form.querySelectorAll('[data-filter-input]:checked').forEach((box) => {
          params.append(box.name, box.value);
        });
        const sort = form.querySelector('[data-sort-select]');
        if (sort && sort.value) params.append('sort_by', sort.value);
        const query = params.toString();
        return form.getAttribute('action') + (query ? '?' + query : '');
      }

      // Re-render for the current form state and push the URL to the history
      applyFilters() {
        this.renderFromUrl(this.buildUrl(), true);
      }

      // Back/forward button → render the restored URL without pushing again
      onPopState() {
        this.renderFromUrl(window.location.href, false);
      }

      // Fetch the rendered section for the URL and swap the content in place
      async renderFromUrl(url, pushToHistory) {
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();
        this.classList.add('is-loading');
        const results = this.querySelector('[data-results]');
        if (results) results.setAttribute('aria-busy', 'true');
        try {
          const separator = url.includes('?') ? '&' : '?';
          const response = await fetch(url + separator + 'section_id=' + this.sectionId, {
            signal: this.abortController.signal,
          });
          if (!response.ok) throw new Error('HTTP ' + response.status);
          const html = await response.text();
          this.swapContent(html);
          if (pushToHistory) window.history.pushState({}, '', url);
        } catch (error) {
          if (error.name === 'AbortError') return;
          // Fallback: keep filtering functional via a normal page load
          window.location.assign(url);
        } finally {
          this.classList.remove('is-loading');
          const freshResults = this.querySelector('[data-results]');
          if (freshResults) freshResults.setAttribute('aria-busy', 'false');
        }
      }

      // Swap inner content, keeping panel state and focus where they were
      swapContent(html) {
        const active = document.activeElement;
        const focusName = active && this.contains(active) ? active.getAttribute('name') : null;
        const focusValue = focusName ? active.value : null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('ndog-collection-filters');
        if (!fresh) throw new Error('section markup missing in response');
        this.innerHTML = fresh.innerHTML;
        this.setupPanel();
        this.restoreFocus(focusName, focusValue);
      }

      // Put focus back on the control the user was interacting with
      restoreFocus(name, value) {
        if (!name) return;
        const exact = value
          ? this.querySelector('[name="' + CSS.escape(name) + '"][value="' + CSS.escape(value) + '"]')
          : null;
        const target = exact || this.querySelector('[name="' + CSS.escape(name) + '"]');
        if (target) target.focus();
      }
    }
  );
}
